import { useId, useState } from 'react';
import { Tag } from 'antd';
import { DownOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';

import { boDau } from '../../../../components/layout/nav-search';
import { Checkbox, Input } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { soVN } from '../../../../lib/dinh-dang';
import type { LuaChon } from './chung';

/** Từ ngần này mục trở lên mới hiện ô tìm — danh sách ngắn thì ô tìm chỉ là thêm một thứ để nhìn. */
const NGUONG_TIM = 8;

/**
 * Chọn nhiều mục từ một danh sách có thể rất dài.
 *
 * Bốn mươi nguồn là bốn mươi dòng, và câu hỏi cần trả lời trước nhất — "bot này ĐANG tra ở đâu" — phải dò từng dòng mới
 * ra. Nên tách hai việc: thứ ĐÃ CHỌN nằm trên cùng thành nhãn gỡ được, danh sách ĐỂ CHỌN nằm trong khung cao cố định có ô
 * tìm. Mục chưa sẵn sàng (nguồn nháp, đang lỗi) gom xuống một nhóm gập: vẫn chọn được, nhưng không chen giữa mục dùng ngay.
 */
export function ChonNhieuNguon({
  nhan, ghiChu, ds, chon, onBat, suaDuoc, tran, chuaSanSang, rong,
}: {
  nhan: string;
  ghiChu?: string;
  ds: LuaChon[];
  chon: string[];
  onBat: (id: string) => void;
  suaDuoc: boolean;
  /** Số mục tối đa được chọn. Bỏ trống = không giới hạn. */
  tran?: number;
  chuaSanSang?: (c: LuaChon) => boolean;
  rong: string;
}) {
  const { t, locale } = useT();
  const idNhom = useId();
  const [tuKhoaTho, setTuKhoa] = useState('');
  const [moNhom, setMoNhom] = useState(false);

  const tuKhoa = boDau(tuKhoaTho.trim());
  const khop = tuKhoa ? ds.filter((c) => boDau(`${c.ten} ${c.id}`).includes(tuKhoa)) : ds;
  const sanSang = khop.filter((c) => !chuaSanSang?.(c));
  const chua = khop.filter((c) => chuaSanSang?.(c));
  const daChon = chon.map((id) => ds.find((c) => c.id === id) ?? { id, ten: id, phu: '' });
  const dayTran = tran !== undefined && chon.length >= tran;
  // Đang tìm thì mở nhóm gập: mục khớp nằm trong nhóm đóng là mục tìm không ra.
  const hienNhom = moNhom || !!tuKhoa;

  const o = (c: LuaChon) => (
    <Checkbox
      key={c.id}
      disabled={!suaDuoc || (!chon.includes(c.id) && dayTran)}
      checked={chon.includes(c.id)}
      onChange={() => onBat(c.id)}
    >
      <span className="text-xs leading-snug">{c.ten}</span>
      {c.phu ? <span className="ms-1 text-xs text-content-muted">({c.phu})</span> : null}
    </Checkbox>
  );

  return (
    <div className="min-w-0">
      <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-2">
        <p className="text-xs font-medium text-content-secondary">
          {nhan}
          {ghiChu ? <span className="ms-1.5 font-normal text-content-muted">{ghiChu}</span> : null}
        </p>
        <span className="text-xs tabular-nums text-content-muted">
          {tran !== undefined
            ? t('troLyAiBot.chonNguon.demTran', { n: soVN(chon.length, locale), tong: soVN(tran, locale) })
            : t('troLyAiBot.chonNguon.dem', { n: soVN(chon.length, locale) })}
        </span>
      </div>

      {ds.length === 0 ? (
        <p className="text-xs text-content-muted">{rong}</p>
      ) : (
        <div className="space-y-2">
          <div className="flex min-h-7 flex-wrap items-center gap-1.5">
            {daChon.length ? (
              daChon.map((c) => (
                <Tag
                  key={c.id}
                  closable={suaDuoc}
                  closeIcon={suaDuoc ? undefined : false}
                  onClose={(e) => {
                    e.preventDefault();
                    onBat(c.id);
                  }}
                  className="m-0 max-w-full truncate text-xs"
                  title={c.ten}
                >
                  {c.ten}
                </Tag>
              ))
            ) : (
              <span className="text-xs text-content-muted">{t('troLyAiBot.chonNguon.chuaChon')}</span>
            )}
          </div>

          {ds.length >= NGUONG_TIM ? (
            <Input
              allowClear
              aria-label={t('troLyAiBot.chonNguon.tim', { nhan })}
              placeholder={t('troLyAiBot.chonNguon.tim', { nhan })}
              prefix={<SearchOutlined className="text-content-muted" />}
              value={tuKhoaTho}
              onChange={(e) => setTuKhoa(e.target.value)}
            />
          ) : null}

          <div className="max-h-64 space-y-2 overflow-y-auto rounded border border-default p-3">
            {khop.length === 0 ? (
              <p className="text-xs text-content-muted">{t('troLyAiBot.chonNguon.khongKhop')}</p>
            ) : null}
            {sanSang.length ? (
              <div className="grid grid-cols-1 items-start gap-x-4 gap-y-2 xl:grid-cols-2">{sanSang.map(o)}</div>
            ) : null}
            {chua.length ? (
              <div className={sanSang.length ? 'border-t border-default pt-2' : undefined}>
                <button
                  type="button"
                  aria-expanded={hienNhom}
                  aria-controls={idNhom}
                  onClick={() => setMoNhom((m) => !m)}
                  className="flex items-center gap-1.5 text-xs font-medium text-content-secondary hover:text-primary-700"
                >
                  {hienNhom ? <DownOutlined /> : <RightOutlined />}
                  {t('troLyAiBot.chonNguon.chuaSanSang', { n: soVN(chua.length, locale) })}
                </button>
                {hienNhom ? (
                  <div id={idNhom} className="mt-2 grid grid-cols-1 items-start gap-x-4 gap-y-2 xl:grid-cols-2">
                    {chua.map(o)}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
